import { Link } from 'react-router-dom';
import { useFetch } from '@/hooks/useFetch';
import ApperIcon from '@/components/ApperIcon';
import { loadProducts } from '@/shared/catalog';
import { productPath } from '@/shared/products';
import { money, productTerms } from '@/shared/pricing';
import { cn } from '@/lib/utils';
import { GLOW, LABEL, WRAP } from '@/components/home/styles';
import { useT } from '@/shared/i18n';

const MAX_ON_HOME = 24;

/** Custom products marked for the front page. Built-in plans have their own sections. */
export function Products() {
  const { t } = useT();
  const { data } = useFetch(loadProducts, []);

  const list = (data ?? [])
    .filter((p) => p.active_c && p.show_on_home_c && !p.plan_key_c && p.slug_c)
    .slice(0, MAX_ON_HOME);
  if (!list.length) return null;

  return (
    <section id="more" className={cn(WRAP, 'scroll-mt-24 pt-28')}>
      <div className="border-t border-border pt-10">
        <p className={LABEL}>{t('More from the house')}</p>
        <h2 className="mt-4 text-[clamp(34px,5vw,56px)] font-light leading-[1.05]">
          {t('Extra spirits.')}
          <span className="block text-muted-foreground">{t('Add-ons and one-offs, each on its own page.')}</span>
        </h2>
        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {list.map((p) => {
            const terms = productTerms(p);
            const from = terms.length ? Math.min(...terms.map((t) => t.price)) : null;
            const single = terms.length === 1 ? terms[0] : null;
            return (
              <Link
                key={p.Id}
                to={productPath(p)}
                className="group flex flex-col rounded-lg border border-border bg-card/60 p-6 backdrop-blur-sm transition-colors hover:border-primary/60"
              >
                {p.image_url_c && (
                  <img
                    src={p.image_url_c}
                    alt=""
                    loading="lazy"
                    className="mb-5 aspect-video w-full rounded-md border border-border object-cover"
                  />
                )}
                <h3 className="font-heading text-4xl leading-none">{p.Name}</h3>
                {p.tagline_c && <p className="mt-3 text-[15px] text-muted-foreground">{p.tagline_c}</p>}
                <div className="mt-auto flex items-end justify-between gap-3 pt-8">
                  <span className="text-sm text-muted-foreground">
                    {single ? (
                      <>
                        <span className={cn('text-3xl font-light', GLOW)}>{money(single.price)}</span>{' '}
                        {single.months === 0 ? t('one-time') : t(single.label).toLowerCase()}
                      </>
                    ) : from != null ? (
                      <>
                        {t('from')} <span className={cn('text-3xl font-light', GLOW)}>{money(from)}</span>
                      </>
                    ) : (
                      t('Ask for a price')
                    )}
                  </span>
                  <span className="flex items-center gap-1 text-sm text-primary">
                    {t('View')}
                    <ApperIcon name="ArrowRight" size={16} className="transition-transform group-hover:translate-x-0.5" />
                  </span>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}
